import React, { useEffect, useState } from 'react';
import { tokenAPI } from '../services/api';
import Pagination from '../components/Pagination';
import TokenDialog from '../components/dialogs/TokenDialog';
import { formatDateTime } from '../utils/dateTime';
import './Instances.css';

interface TokenItem {
  _id: string;
  name: string;
  token: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

function Tokens() {
  const [tokens, setTokens] = useState<TokenItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', active: true });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [notification, setNotification] = useState<{message: string, type: 'success' | 'error'} | null>(null);
  const [searchName, setSearchName] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showDialog, setShowDialog] = useState(false);

  const showNotification = (message: string, type: 'success' | 'error') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  useEffect(() => {
    loadTokens();
  }, []);

  const loadTokens = async () => {
    setLoading(true);
    try {
      const response = await tokenAPI.list();
      setTokens(response.data);
    } catch (error) {
      console.error('Erro ao carregar tokens:', error);
      showNotification('Erro ao carregar tokens', 'error');
    } finally {
      setLoading(false);
    }
  };

  const filteredTokens = tokens.filter(token =>
    token.name.toLowerCase().includes(searchName.toLowerCase())
  );

  const totalPages = Math.ceil(filteredTokens.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTokens = filteredTokens.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingId) {
        await tokenAPI.update(editingId, formData);
        showNotification('Token atualizado com sucesso!', 'success');
      } else {
        await tokenAPI.create(formData);
        showNotification('Token criado com sucesso!', 'success');
      }

      closeDialog();
      loadTokens();
    } catch (error: any) {
      console.error('Erro ao salvar token:', error);
      const msg = error?.response?.data?.detail || 'Erro ao salvar token';
      showNotification(msg, 'error');
    }
  };

  const handleEdit = (token: TokenItem) => {
    setFormData({
      name: token.name,
      active: token.active
    });
    setEditingId(token._id);
    setShowDialog(true);
  };

  const handleNewToken = () => {
    setFormData({ name: '', active: true });
    setEditingId(null);
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
    setEditingId(null);
    setFormData({ name: '', active: true });
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Tem certeza que deseja deletar este token?')) {
      try {
        await tokenAPI.delete(id);
        showNotification('Token deletado com sucesso!', 'success');
        loadTokens();
      } catch (error) {
        console.error('Erro ao deletar:', error);
        showNotification('Erro ao deletar token', 'error');
      }
    }
  };

  const copyToken = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      showNotification('Token copiado para a área de transferência', 'success');
    } catch (error) {
      console.error('Erro ao copiar token:', error);
      showNotification('Não foi possível copiar o token', 'error');
    }
  };

  return (
    <div className="instances-container">
      <div className="page-header">
        <h1>Tokens de Integração</h1>

        <button 
          className="btn-add"
          onClick={handleNewToken}
          title="Gerar novo token"
        >
          <span className="btn-icon">+</span>
          <span>Novo Token</span>
        </button>
      </div>

      <TokenDialog
        show={showDialog}
        editingId={editingId}
        formData={formData}
        onSubmit={handleSubmit}
        onChange={(data) => setFormData({ ...formData, ...data })}
        onClose={closeDialog}
      />

      <div className="search-bar">
        <div className="search-input-wrapper">
          <input
            type="text"
            placeholder="🔍 Buscar por nome..."
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            className="search-input"
          />
          {searchName && (
            <button 
              className="clear-search"
              onClick={() => setSearchName('')}
              title="Limpar busca"
            >
              ✕
            </button>
          )}
        </div>
        <span className="search-count">
          {filteredTokens.length} token{filteredTokens.length !== 1 ? 's' : ''}
        </span>
      </div>

      {loading ? (
        <p>Carregando...</p>
      ) : (
        <>
          {paginatedTokens.length > 0 ? (
            <div className="table-container">
              <table className="instances-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Token</th>
                    <th>Status</th>
                    <th>Criado</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTokens.map((token) => (
                    <tr key={token._id}>
                      <td>
                        <span className="name-icon">🔑</span>
                        {token.name}
                      </td>
                      <td className="url-cell" title={token.token}>
                        {token.token}
                      </td>
                      <td>
                        <span
                          className="default-badge"
                          style={{
                            background: token.active ? '#34d399' : '#f87171',
                            color: '#111827'
                          }}
                        >
                          {token.active ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td>{formatDateTime(token.created_at)}</td>
                      <td className="actions-cell">
                        <button
                          className="btn-action btn-edit"
                          onClick={() => copyToken(token.token)}
                          title="Copiar token"
                        >
                          📋
                        </button>
                        <button 
                          className="btn-action btn-edit"
                          onClick={() => handleEdit(token)}
                          title="Editar token"
                        >
                          ✏️
                        </button>
                        <button 
                          className="btn-action btn-delete"
                          onClick={() => handleDelete(token._id)}
                          title="Deletar token"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-results">
              Nenhum token encontrado{searchName && ` com o nome "${searchName}"`}
            </div>
          )}

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      {notification && (
        <div className={`notification ${notification.type}`}>
          <span>{notification.type === 'success' ? '✅' : '❌'}</span>
          {notification.message}
        </div>
      )}
    </div>
  );
}

export default Tokens;

